/**
 * Downloads a file from blob data in an axios response (requires the full response object)
 *
 * Uses the `content-disposition` header to determine the filename
 * and the `content-type` header to determine the file type
 *
 * Works with `"attachment"` and `"inline"` content-disposition types
 *
 * Handles IE11 using msSaveOrOpenBlob
 *
 * @param {object} response - axios response object with blob data
 * @param {object} response.headers - axios response headers
 * @param {Blob} response.data - blob data of the file
 * @return {void}
 * @throws Will throw an error if content type or content disposition headers are missing or malformed
 */

const downloadFromBlobData = (response) => {
  const type = response.headers['content-type'];

  if (!type) {
    throw new Error('Could not find content type in response headers');
  }

  const contentDisposition =
    response.headers['content-disposition'] ??
    response.headers['Content-Disposition'];

  if (!contentDisposition) {
    throw new Error('Could not find content disposition in response headers');
  }

  let filename;

  if (
    (contentDisposition && contentDisposition.includes('attachment')) ||
    contentDisposition.includes('inline')
  ) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);

    if (!filenameMatch) {
      throw new Error('Could not extract filename from content disposition');
    }

    [, filename] = filenameMatch;
  }

  const blob = new Blob([response.data], { type });

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const link = document.createElement('a');

    const urlObject = window.URL.createObjectURL(blob);

    link.href = urlObject;
    link.download = filename;

    if (typeof link.download === 'undefined') link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(urlObject);
    }, 100);
  }
};

export default downloadFromBlobData;
